import { Alert } from '@Pages/pageRuntime/components/ui/Alert';
import { AlertDescription } from '@Pages/pageRuntime/components/ui/AlertDescription';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { FormTextInput } from '@Pages/pageRuntime/components/ui/form/FormTextInput';
import { Skeleton } from '@Pages/pageRuntime/components/ui/Skeleton';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { apiErrorMessage } from '@Pages/pageRuntime/sdk/apiErrors';
import type { SdkApiError } from '@Pages/pageRuntime/sdk/types';
import type React from 'react';
import { type FormEventHandler } from 'react';

import { FormCodeInput } from './forms/CodeInput';

export function TwoFactorLoading({ label, className }: { label: string; className?: string }) {
  return (
    <div role="status" aria-busy="true" className={cn('grid gap-3', className)}>
      <span className="sr-only">{label}</span>
      <Skeleton className="h-5 w-64" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

export function TwoFactorLoadError({
  error,
  fallback,
  className
}: {
  error: SdkApiError;
  fallback: string;
  className?: string;
}) {
  return (
    <Alert variant="destructive" className={className}>
      <AlertDescription>{apiErrorMessage(error, fallback)}</AlertDescription>
    </Alert>
  );
}

/** The pending sign-in or setup ended, so the only way on is to start again. */
export function TwoFactorRestart({
  error,
  fallback,
  label,
  onRestart,
  disabled,
  className,
  buttonClassName
}: {
  error: SdkApiError | null;
  fallback: string;
  label: string;
  onRestart: () => void;
  disabled: boolean;
  className?: string;
  buttonClassName?: string;
}) {
  return (
    <div className={cn('grid gap-3', className)}>
      <Alert variant="destructive">
        <AlertDescription>{apiErrorMessage(error, fallback)}</AlertDescription>
      </Alert>
      <Button type="button" className={buttonClassName} onClick={onRestart} disabled={disabled}>
        {label}
      </Button>
    </div>
  );
}

export function TwoFactorLinkButton({
  onClick,
  disabled,
  children
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="link"
      onClick={onClick}
      disabled={disabled}
      className="h-auto px-2 py-1 text-muted-foreground hover:text-foreground"
    >
      {children}
    </Button>
  );
}

type TwoFactorFactorFormProps = {
  /** Whether the form takes a backup code instead of the 6-digit SMS code. */
  usesBackupCode: boolean;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  /** Submit the SMS code as soon as its last digit lands. */
  submitOnComplete?: boolean;
  codeLabel?: React.ReactNode;
  submitLabel: React.ReactNode;
  errors: string[];
  disabled: boolean;
  buttonClassName?: string;
};

/** One factor entry: the SMS code in one cell per digit, or a typed backup code. */
export function TwoFactorFactorForm({
  usesBackupCode,
  value,
  onChange,
  onSubmit,
  submitOnComplete = false,
  codeLabel = 'Verification code',
  submitLabel,
  errors,
  disabled,
  buttonClassName
}: TwoFactorFactorFormProps) {
  const submit: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onSubmit(value);
  };
  const complete = usesBackupCode ? value.trim() !== '' : value.length === 6;

  return (
    <form onSubmit={submit} className="grid gap-2">
      {usesBackupCode ? (
        <FormTextInput
          label="Backup code"
          placeholder="XXXXX-XXXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={32}
          value={value}
          onChange={onChange}
          error={errors[0]}
        />
      ) : (
        <FormCodeInput
          label={codeLabel}
          value={value}
          onChange={onChange}
          onComplete={submitOnComplete ? onSubmit : undefined}
          disabled={disabled}
          error={errors}
        />
      )}
      <Button
        type="submit"
        className={cn('mt-2 w-full', buttonClassName)}
        disabled={disabled || !complete}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
